import { useAuth } from "@frontegg/react";
import Role, { ROLE_RANK, getRoleRank, isPlatformRole } from "enums/Role";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { hasPlatformApp } from "utils/apps";

export interface AssignableRoleOption {
  role: Role;
  disabled: boolean;
}

export interface UseAssignableRolesResult {
  options: AssignableRoleOption[];
  // True when the edited user outranks the actor: the whole role field is locked.
  fieldDisabled: boolean;
  // True when platform roles were withheld only because the account lacks the app.
  platformAppMissing: boolean;
}

/**
 * Highest authority (lowest ROLE_RANK) among the logged-in user's roles.
 * Returns Infinity if the user has no recognized role (i.e. can assign nothing).
 */
export const useActorRoleRank = (): number => {
  const { user } = useAuth();
  const ranks = (user?.roles ?? [])
    .map((r: { key: string }) => ROLE_RANK[r.key as Role])
    .filter(
      (rank: number | undefined): rank is number => typeof rank === "number",
    );
  return ranks.length ? Math.min(...ranks) : Number.POSITIVE_INFINITY;
};

/**
 * Decide which roles a user may assign in a role dropdown.
 *
 * @param candidateRoles roles valid for this context (company-only vs. + platform)
 * @param currentRole the edited user's existing role (omit/empty when adding)
 * @param targetCompanyId the company the role is being assigned in; omit when it is not
 *   the actor's own company (a new account, or none picked yet)
 *
 * Enforces (see ROLE_RANK, lower number = higher authority):
 *  - You cannot assign a role with higher authority than your own.
 *  - A PlatformAdmin can only grant a platform role inside their own company; granting
 *    one elsewhere takes a PlatformOwner. Mirrors assertCanAssignRoleInCompany on the server.
 *  - You cannot change the role of a user who outranks you (fieldDisabled).
 *  - Roles the actor cannot assign are left out of the list, except the edited user's
 *    current role, which is always shown (disabled) so the dropdown is never blank.
 */
export const useAssignableRoles = (
  candidateRoles: Role[],
  currentRole?: Role,
  targetCompanyId?: string,
  targetAppFeatureKeys?: ReadonlySet<string> | null,
): UseAssignableRolesResult => {
  const actorRank = useActorRoleRank();
  const { user: actor } = useGetLoggedInUser();

  const validCurrentRole =
    currentRole && currentRole in ROLE_RANK ? currentRole : undefined;

  const outranksActor = (role: Role) => getRoleRank(role) < actorRank;

  const platformAppMissing = !hasPlatformApp(targetAppFeatureKeys);

  const canAssign = (role: Role) => {
    if (outranksActor(role)) return false;
    if (!isPlatformRole(role)) return true;
    if (platformAppMissing) return false;
    if (targetCompanyId && targetCompanyId === actor?.companyId) return true;
    return actorRank <= ROLE_RANK[Role.PlatformOwner];
  };

  const roles = candidateRoles.filter(canAssign);
  if (validCurrentRole && !roles.includes(validCurrentRole)) {
    roles.push(validCurrentRole);
  }

  const fieldDisabled =
    validCurrentRole !== undefined && getRoleRank(validCurrentRole) < actorRank;

  return {
    fieldDisabled,
    platformAppMissing,
    options: roles.map((role) => ({
      role,
      disabled: !canAssign(role),
    })),
  };
};
